import { Contact } from '~api/address_book';

import { useState } from 'react';

import { ListLayout, Modal, ModalSelect, OptionList, PileBadge, SearchInput, Spinner } from '@ingrid/luterkort-react';

import { AddressMultiline } from '~src/components/AddressMultiline/AddressMultiline';
import { SelectModal, SelectModalProps } from '~src/components/Modal/Modal';
import { LocalizationKey, useTranslation } from '~src/i18n/use-translation';
import { useAddressBookListQuery } from '~src/pages/settings/address-book/queries';

import { AddressType } from '../types';
import { AddressCreate } from './address-create';
import { useAddresses } from './addresses-context';

export type AddressSelectProps = {
  value?: Contact;
  onValueChange: (contact?: Contact) => void;
  type: AddressType;
} & Pick<SelectModalProps, 'trigger'>;

export function AddressSelect({ type, value, onValueChange, ...modalProps }: AddressSelectProps) {
  const { t } = useTranslation();
  const { contacts: contextContacts } = useAddresses();
  const [search, setSearch] = useState<string>();
  const { data, isLoading } = useAddressBookListQuery({ query: search }, !!search);

  const filteredContextContacts = search
    ? contextContacts.filter(a => a.address?.name?.toLowerCase().includes(search.toLowerCase()))
    : contextContacts;

  const combinedContacts = [...filteredContextContacts, ...(data?.contacts ?? [])];

  return (
    <SelectModal
      filterFactory={false}
      value={value?.id ? [value.id] : ['']}
      onValueChange={val => onValueChange(combinedContacts.find(c => c.id === val[0]))}
      searchValue={search}
      onSearchValueChange={setSearch}
      {...modalProps}
    >
      <Modal.Header
        title={t('shipments.address.searchHeader', {
          item: t(`shipments.address.type.${type}` as LocalizationKey),
        })}
      />

      <ModalSelect.Content>
        <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
          <ModalSelect.Control>
            <SearchInput placeholder={t('shipments.address.searchPlaceholder')} />
          </ModalSelect.Control>

          {isLoading && <Spinner />}

          {combinedContacts.length > 0 && !isLoading && (
            <OptionList.Viewport>
              {combinedContacts.map(({ id, address }) => (
                <OptionList.Item spacing="sm" key={`address-${id}`} value={id!}>
                  <ListLayout.Stack>
                    <ListLayout.SubRow gap="sm">
                      <ListLayout.PrimaryText
                        css={{
                          lineHeight: '16px',
                        }}
                      >
                        {address?.name}
                      </ListLayout.PrimaryText>
                      <PileBadge>
                        {id?.includes('shipment')
                          ? t('common.shipment')
                          : id?.includes('template')
                            ? t('common.template')
                            : t('common.addressBook')}
                      </PileBadge>
                    </ListLayout.SubRow>
                    <ListLayout.SecondaryText>
                      <AddressMultiline
                        address={address}
                        template={[
                          'address_line_1',
                          'newline',
                          'address_line_2',
                          'newline',
                          'city',
                          'comma',
                          'postal_code',
                          'comma',
                          'country',
                        ]}
                      />
                    </ListLayout.SecondaryText>
                  </ListLayout.Stack>
                </OptionList.Item>
              ))}
            </OptionList.Viewport>
          )}
        </div>
      </ModalSelect.Content>

      <Modal.Footer css={{ justifyContent: 'start' }}>
        <AddressCreate type={type} />
      </Modal.Footer>
    </SelectModal>
  );
}
